import { useMemo } from "react";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import type { SortSelection } from "@src/features/clips/types/camera";
import { filterCamerasByQuery, type CameraFilterField } from "@src/features/cameras/utils/camera-filter";
import { sortCameras } from "@src/features/cameras/utils/camera-sort";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import type { SignedSnapshot, SnapshotsByCameraKey } from "@visioni/a3s-media/app";

interface UseCameraCollectionParams {
  cameras?: CameraApiResponse[];
  searchQuery: string;
  sortBy: SortSelection;
  filterField?: CameraFilterField;
}

export type CameraGridItem = { camera: CameraApiResponse; snapshot: SignedSnapshot | undefined } | undefined;

/**
 * Groups cameras by consecutive `key.asset` runs and lays them out on a grid,
 * padding with `undefined` slots so that cameras sharing an asset stay on the
 * same row whenever possible. Adjacent groups that together fit within a row
 * are packed without padding.
 */
export function buildGroupedGridItems(
  cameras: CameraApiResponse[],
  numColumns: number,
  snapshots: SnapshotsByCameraKey | undefined,
): CameraGridItem[] {
  if (cameras.length === 0 || numColumns <= 0) return [];

  const groups: CameraApiResponse[][] = [];
  for (const camera of cameras) {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup[0].key.asset === camera.key.asset) {
      lastGroup.push(camera);
    } else {
      groups.push([camera]);
    }
  }

  const result: CameraGridItem[] = [];
  let col = 0;

  for (const group of groups) {
    const remaining = numColumns - col;

    if (group.length <= remaining) {
      for (const camera of group) {
        result.push({ camera, snapshot: snapshots?.[`${camera.key.asset}@${camera.key.id}`] });
        col++;
      }
    } else if (col === 0) {
      for (const camera of group) {
        result.push({ camera, snapshot: snapshots?.[`${camera.key.asset}@${camera.key.id}`] });
        col = (col + 1) % numColumns;
      }
    } else {
      while (col < numColumns) {
        result.push(undefined);
        col++;
      }
      col = 0;
      for (const camera of group) {
        result.push({ camera, snapshot: snapshots?.[`${camera.key.asset}@${camera.key.id}`] });
        col = (col + 1) % numColumns;
      }
    }

    if (col === numColumns) {
      col = 0;
    }
  }

  return result;
}

export function useCameraCollection({ cameras, searchQuery, sortBy, filterField }: UseCameraCollectionParams) {
  const filteredCameras = useMemo(() => {
    if (!cameras) return [];
    return filterCamerasByQuery(cameras, searchQuery, filterField === "All" ? undefined : filterField);
  }, [cameras, searchQuery, filterField]);

  const sortedCameras = useMemo(() => {
    if (filteredCameras.length === 0) return [];
    return sortCameras(filteredCameras, sortBy.length === 0 ? "id" : sortBy);
  }, [filteredCameras, sortBy]);

  const groupedCameras = useMemo(() => {
    if (filteredCameras.length === 0) return [];
    const normalizedSort = sortBy.length === 0 ? "id" : sortBy;
    return groupCamerasBySite(filteredCameras, normalizedSort).map((group) => ({
      ...group,
      thumbnailUrl: group.cameras[0]?.snapshot?.url ?? null,
    }));
  }, [filteredCameras, sortBy]);

  return {
    filteredCameras,
    sortedCameras,
    groupedCameras,
    hasData: cameras !== undefined && cameras.length > 0,
  };
}
